import { useNavigate } from "react-router-dom";
import { useAuthStore } from "@/features/auth/store/auth.store";
import { getPostAuthLanding } from "@/features/auth/utils/post-auth-redirect";
import { FloatPanel, StudioButton } from "@/features/studio/theme";

/**
 * WelcomeScreen - Revamped to match the editorial designer studio look.
 */
export function WelcomeScreen() {
	const navigate = useNavigate();
	const { isAuthenticated, needsOnboarding, needsProfileSetup, getUserType } =
		useAuthStore();

	const handleExplore = () => {
		if (isAuthenticated()) {
			if (needsOnboarding()) {
				navigate("/onboarding", { replace: true });
			} else if (needsProfileSetup()) {
				navigate(`/onboarding/${getUserType()}`, { replace: true });
			} else {
				navigate(getPostAuthLanding(getUserType()), { replace: true });
			}
		} else {
			navigate("/login", { replace: true });
		}
	};

	return (
		<div className="relative flex min-h-screen flex-col items-center justify-center px-4 py-8 bg-dashboard-warm-350">
			{/* Decorative Diamond Elements - Scattered rotated squares */}
			<div className="absolute inset-0 pointer-events-none overflow-hidden hidden md:block">
				{/* ... */}
				{/* Top-left area */}
				<div className="absolute opacity-20 top-[20%] left-[10%] w-2 h-2 bg-dashboard-accent rotate-45" />
				<div className="absolute opacity-35 top-[25%] left-[15%] w-2.5 h-2.5 bg-dashboard-muted rotate-45" />

				{/* Top-right area */}
				<div className="absolute opacity-15 top-[22%] right-[12%] w-3 h-3 bg-dashboard-ink rotate-45" />
				<div className="absolute opacity-40 top-[28%] right-[18%] w-2 h-2 bg-dashboard-muted rotate-45" />

				{/* Middle-left */}
				<div className="absolute opacity-25 top-[45%] left-[8%] w-2.5 h-2.5 bg-dashboard-accent rotate-45" />

				{/* Middle-right */}
				<div className="absolute opacity-20 top-[50%] right-[10%] w-2.25 h-2.25 bg-dashboard-muted rotate-45" />
				<div className="absolute opacity-30 top-[58%] right-[16%] w-2 h-2 bg-dashboard-ink rotate-45" />

				{/* Bottom-left */}
				<div className="absolute opacity-25 bottom-[25%] left-[12%] w-2.75 h-2.75 bg-dashboard-accent rotate-45" />
				<div className="absolute opacity-35 bottom-[30%] left-[20%] w-2 h-2 bg-dashboard-muted rotate-45" />

				{/* Bottom-right */}
				<div className="absolute opacity-20 bottom-[22%] right-[15%] w-2.5 h-2.5 bg-dashboard-ink rotate-45" />

				{/* Extra scattered diamonds for more visual interest */}
				<div className="absolute opacity-15 top-[35%] left-[25%] w-1.5 h-1.5 bg-dashboard-accent rotate-45" />
				<div className="absolute opacity-30 top-[40%] right-[25%] w-1.75 h-1.75 bg-dashboard-muted rotate-45" />
				<div className="absolute opacity-25 bottom-[35%] left-[18%] w-2.25 h-2.25 bg-dashboard-ink rotate-45" />
				<div className="absolute opacity-20 bottom-[40%] right-[22%] w-2 h-2 bg-dashboard-accent rotate-45" />
			</div>

			{/* Main Content - Centered Card */}
			<div className="w-full max-w-150 relative z-10">
				<FloatPanel elevation={3} padding="48px 40px" className="text-center">
					{/* Brand Name in Card */}
					<h2 className="font-display font-semibold text-3xl tracking-[0.15em] uppercase text-dashboard-text mb-4">
						Finishd
					</h2>

					{/* Subtitle */}
					<p className="font-body text-base leading-relaxed text-dashboard-muted-alt mb-8">
						Let's personalize your experience so rituals feel more meaningful to
						you.
					</p>

					{/* Start Exploring Button */}
					<div className="flex justify-center">
						<StudioButton
							variant="primary"
							size="lg"
							onClick={handleExplore}
							className="w-full sm:w-70"
						>
							Start exploring
						</StudioButton>
					</div>
				</FloatPanel>
			</div>
		</div>
	);
}
